import React, { createContext, useState, useEffect, useCallback } from 'react';

// Create context with default values
export const AppContext = createContext({
    settings: null,
    loading: true,
    error: null,
    refresh: () => { },
});

/**
 * AppProvider
 * - Fetches /config.json from public folder (or a custom URL via `src` prop)
 * - Exposes { settings, loading, error, refresh }
 */
export const AppProvider = ({ children, src = '/config.json' }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to fetch and set config
    const fetchConfig = useCallback(
        async (signal) => {
            setLoading(true);
            setError(null);

            try {
                const res = await fetch(src, { signal, cache: 'no-cache' });

                if (!res.ok) {
                    throw new Error(`Failed to fetch config: ${res.status} ${res.statusText}`);
                }

                const json = await res.json();

                // Normalize settings shape
                const normalized = {
                    brandName: json.brandName || null,
                    email: json.email || null,
                    phone: json.phone || null,
                    instagram: json.instagram || null,
                    facebook: json.facebook || null,
                    linkedin: json.linkedin || null,
                    address: json.address || null,
                    raw: json, // Keep full JSON in case you need extra fields later
                };

                setSettings(normalized);
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('AppContext fetch error:', err);
                    setError(err.message || 'Failed to load config');
                }
            } finally {
                setLoading(false);
            }
        },
        [src]
    );

    // Fetch once on mount
    useEffect(() => {
        const controller = new AbortController();
        fetchConfig(controller.signal);
        return () => controller.abort();
    }, [fetchConfig]);

    // Manual refresh (if needed)
    const refresh = useCallback(() => {
        const controller = new AbortController();
        fetchConfig(controller.signal);
    }, [fetchConfig]);

    return (
        <AppContext.Provider value={{ settings, loading, error, refresh }}>
            {children}
        </AppContext.Provider>
    );
};
